// DOM menus. Menus never pause the shared world. Screens live in ./menus/*; this file owns the frame (title bar,
// tabs, footer), what is selected, the "unchanged HTML keeps the DOM" guard and the click/input plumbing.
import type { Character } from '../../common/src/rules';
import { derived } from '../../common/src/rules';
import type { ActRequest, PagerMsg, Snapshot } from '../../common/src/protocol';
import { playerLook } from './art';
import { CHAR_W, CHAR_H, personCanvas } from './hd-sprites';
import { Ctx, Screen, View, Settings, chip, esc, money, nearPoi, newSeed, resetRid } from './menus/kit';
import { inventory } from './menus/inventory';
import { skills, skillBadge } from './menus/skills';
import { jobs } from './menus/jobs';
import { crew } from './menus/crew';
import { lore } from './menus/lore';
import { settings, ZOOMS } from './menus/settings';
import { vendor, HACHI_SUBS } from './menus/vendors';
import { maskIndex, maskUrl } from './crew';
import { menuIcon } from './menus/visuals';

export { esc, ZOOMS };
/** 'vendor' is not a tab: it is the location screen ([E] at a counter), chosen by `poi`. */
export type Tab = 'inventory' | 'skills' | 'jobs' | 'crew' | 'lore' | 'settings' | 'vendor';
const TABS = ['inventory', 'skills', 'jobs', 'crew', 'lore', 'settings'] as const satisfies readonly Tab[];
const VENDORS = ['weapons', 'clinic', 'garage', 'chop', 'taxi', 'koban', 'broker', 'gunsmith'];

export class UI {
  panel = document.getElementById('panel')!;
  feed = document.getElementById('feed')!;
  chat = document.getElementById('chat') as HTMLInputElement;
  open = false; tab: Tab = 'inventory'; poi = '';
  view: View = { sub: '', item: '', perk: '', filter: 'all', sort: 'rarity', gs: '' };
  char: Character | null = null; snap: Snapshot | null = null; code = ''; you = 0;
  settings: Settings = { crt: true, lowFx: false, volume: 0.7, radio: 0.5, sfx: 0.6, mute: false, radioMute: false, zoom: 1, light: true, neon: 2, goal: true }; // neon 2 = Low: the default comfort level for now (owner, 2026-10-11)
  onSettings: () => void = () => {};
  onReplayTutorial: () => void = () => {};
  onReportBug: () => void = () => {};
  private lastRender = 0; private lastHtml = ''; private lastScreen = '';
  constructor(private send: (a: ActRequest) => void) {
    this.panel.addEventListener('click', e => this.click(e));
    this.panel.addEventListener('input', e => this.onInput(e));
    try { Object.assign(this.settings, JSON.parse(localStorage.getItem('nk.settings') ?? '{}')); } catch { /* ignore */ }
  }
  saveSettings() { try { localStorage.setItem('nk.settings', JSON.stringify(this.settings)); } catch { /* ignore */ } this.onSettings(); }

  pager(m: PagerMsg) {
    const d = document.createElement('div');
    d.className = `msg ${m.kind}`;
    d.innerHTML = m.kind === 'chat' ? `<b>${esc(m.from ?? '')}:</b> ${esc(m.text)}` : esc(m.text);
    if (m.col) d.style.color = m.col;
    this.feed.appendChild(d);
    while (this.feed.children.length > 9) this.feed.firstChild!.remove();
    setTimeout(() => d.classList.add('old'), 12000);
  }
  /** Open a tab, or close the menu when it is already showing. `tab` 'vendor' with a `poi` opens that location's screen. */
  toggle(tab?: Tab, poi?: string) {
    if (tab && (!this.open || this.tab !== tab)) { this.open = true; this.setTab(tab); }
    else this.open = !this.open;
    if (poi !== undefined) this.poi = poi;
    this.panel.style.display = this.open ? 'block' : 'none';
    this.render(true);
  }
  private setTab(t: Tab) { this.tab = t; this.view.sub = ''; const content=this.panel.querySelector('.mn-content');if(content)content.scrollTop=0; }
  private get atVendor() { return this.tab === 'vendor' && VENDORS.includes(this.poi); }
  /** Shoulder buttons: next top tab, or next sub-tab inside Hachi's. */
  cycleTab(d: number) {
    if (this.atVendor) {
      if (this.poi === 'weapons') { const ids = HACHI_SUBS.map(s => s[0]), i = Math.max(0, ids.indexOf(this.view.sub)); this.view.sub = ids[(i + d + ids.length) % ids.length]; }
    } else { const i = TABS.indexOf(this.tab as typeof TABS[number]); this.setTab(TABS[(i + d + TABS.length) % TABS.length]); }
    this.render(true);
  }
  focusMove(d: number) {
    const els = [...this.panel.querySelectorAll<HTMLElement>('button:not([disabled]), input, select')];
    if (!els.length) return;
    const i = els.indexOf(document.activeElement as HTMLElement);
    els[(i + d + els.length) % els.length].focus();
  }
  activate() { (document.activeElement as HTMLElement | null)?.click(); }

  private ctx(c: Character): Ctx {
    const me = this.snap?.p.find(p => p.id === this.you);
    return { c, d: derived(c), snap: this.snap, me, you: this.you, code: this.code, view: this.view, settings: this.settings, near: k => nearPoi(me, k) };
  }
  private screen(m: Ctx): Screen {
    if (this.atVendor) return vendor(m, this.poi);
    switch (this.tab) {
      case 'skills': return skills(m);
      case 'jobs': return jobs(m);
      case 'crew': return crew(m);
      case 'lore': return lore(m);
      case 'settings': return settings(m);
      default: return inventory(m);
    }
  }

  render(force = false) {
    if (!this.open || !this.char) return;
    if (!force && performance.now() - this.lastRender < 500) return;
    // keep focus position across re-renders for gamepad navigation
    const focused = document.activeElement as HTMLElement | null;
    const fKey = focused && this.panel.contains(focused) ? focused.dataset.k : null;
    this.lastRender = performance.now();
    const c = this.char, m = this.ctx(c);
    resetRid();
    const s = this.screen(m), vend = this.atVendor;
    const badge = skillBadge(c);
    const nav = TABS.map((t,i) => `<button class="tab ${t === this.tab ? 'on' : ''}" data-tab="${t}" data-k="tab-${t}" aria-current="${t === this.tab ? 'page' : 'false'}">${menuIcon(t)}<span>${t}</span>${t === 'skills' && badge ? `<b class="mn-badge">${badge}</b>` : `<small>0${i+1}</small>`}</button>`).join('');
    const mi=m.snap?maskIndex(m.snap.p,m.you):0;
    const html = `<div class="mn-shell"><header class="mn-top"><div class="mn-brand">NEON KABUKI <span>2089</span></div><span class="mn-live">CITY LIVE <span> · menus do not pause</span></span><button class="tab close" data-close="1" data-k="close" aria-label="Close menu">Close ×</button></header>
      <aside class="mn-side"><div class="mn-runner"><img alt="" src="${maskUrl(mi)}"><div><b>${esc(c.name)}</b><span>LEVEL ${c.level} / RUNNER</span></div></div><nav class="tabs" aria-label="Runner menus">${nav}${vend?`<button class="tab on" aria-current="page" data-tab="vendor" data-k="tab-vendor">${menuIcon('vendor')}<span>At the counter</span></button>`:''}</nav><div class="mn-side-foot">ROOM <b>${esc(this.code)}</b><span>SHINJUKU / 2089</span></div></aside>
      <main class="mn-content" data-screen="${vend?this.poi:this.tab}"><div class="mn-head"><div class="mn-title"><h2>${esc(s.title)}</h2>${s.subs ?? ''}<div class="mn-chips">${chip('Cash', money(c.money),'warn')}${chip('Cred', String(c.cred))}${s.chips ?? ''}</div></div></div><div class="body mn-body">${s.body}</div></main><footer class="mn-foot"><span>${s.foot ?? ''}</span><span class="mn-foot-key">TAB / CLOSE</span></footer></div>`;
    if (html === this.lastHtml) return; // unchanged: keep the live DOM so in-flight taps are not lost
    this.lastHtml = html;
    const screenKey=`${this.tab}:${this.poi}:${this.view.sub}`;
    const scroll = this.lastScreen===screenKey ? this.panel.querySelector('.mn-content')?.scrollTop ?? 0 : 0;
    this.lastScreen=screenKey;
    this.panel.innerHTML = html;
    const content=this.panel.querySelector('.mn-content');if(content)content.scrollTop=scroll;
    if (fKey) (this.panel.querySelector(`[data-k="${fKey}"]`) as HTMLElement | null)?.focus({preventScroll:true});
    this.afterRender();
  }

  afterRender() {
    const f = document.getElementById('figure') as HTMLCanvasElement | null;
    if (f && this.char) { // full figure wearing the crew mask, 2x
      const a = this.char.appearance, fg = f.getContext('2d')!; fg.imageSmoothingEnabled = false; fg.clearRect(0, 0, f.width, f.height);
      const k = Math.min(f.width / CHAR_W, f.height / CHAR_H), w = CHAR_W * k, h = CHAR_H * k;
      fg.drawImage(personCanvas(playerLook([a.skin, a.jacket, a.hair], Number(f.dataset.crew)), 'S', 0), (f.width - w) / 2, f.height - h, w, h);
    }
  }
  private click(e: Event) {
    const el = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null; if (!el || el.disabled) return;
    if (el.dataset.close) { if (this.open) this.toggle(); return; }
    if (el.dataset.view) { // a menu-only choice: sub-tab, selection, filter, sort
      const i = el.dataset.view.indexOf(':'); (this.view as unknown as Record<string, string>)[el.dataset.view.slice(0, i)] = el.dataset.view.slice(i + 1);
      if(el.dataset.view.startsWith('sub:')){const content=this.panel.querySelector('.mn-content');if(content)content.scrollTop=0;}
      this.render(true);
      if(innerWidth<=1000&&(el.dataset.view.startsWith('item:')||el.dataset.view.startsWith('perk:')))this.panel.querySelector('.mn-detail')?.scrollIntoView({block:'start'});
      return;
    }
    if (el.dataset.tab) { this.setTab(el.dataset.tab as Tab); this.render(true); return; }
    if (el.dataset.ap && this.char) {
      const [f, v] = el.dataset.ap.split(':'); const a = { ...this.char.appearance, [f]: Number(v) };
      this.send({ t: 'appearance', ap: [a.skin, a.jacket, a.hair, a.portrait] }); return;
    }
    if (el.dataset.act) {
      const a = JSON.parse(el.dataset.act);
      if (a.tut) { this.onReplayTutorial(); return; }
      if (a.bug) { this.onReportBug(); return; }
      if (a.copy) { if(navigator.clipboard)void navigator.clipboard.writeText(a.copy).then(()=>{el.textContent='Copied ✓';}).catch(()=>{el.textContent='Select the link to copy';});else el.textContent='Select the link to copy'; return; }
      if (a.dev === 'bots') { void fetch('/api/dev/bots', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code: this.code }) }); return; }
      // regenerate request ids so a second click is a new purchase, while a network retry of the same message is deduplicated
      this.send(a); newSeed();
      setTimeout(() => this.render(true), 150);
    }
  }
  private onInput(e: Event) {
    const el = e.target as HTMLInputElement;
    if (el.dataset.mod) { // a gunsmith dropdown: the request id was fixed at render, so a retry is deduplicated and the next change is new
      const m = JSON.parse(el.dataset.mod);
      this.send({ t: 'mod', req: el.dataset.req!, uid: m.uid, path: m.path, part: el.value || null }); newSeed();
      setTimeout(() => this.render(true), 150); return;
    }
    const k = el.dataset.set as keyof UI['settings'] | undefined; if (!k) return;
    (this.settings as unknown as Record<string, unknown>)[k] = el.type === 'checkbox' ? el.checked : Number(el.value);
    const output=el.parentElement?.querySelector('output');if(output)output.textContent=`${Math.round(Number(el.value)*100)}%`;
    this.saveSettings();
  }
}
